import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

const skeletonStyles = cva('block animate-pulse bg-bg-muted motion-reduce:animate-none', {
  variants: {
    shape: {
      rect: 'rounded-md',
      text: 'rounded-sm',
      circle: 'rounded-full',
      pill: 'rounded-full',
    },
    tone: {
      muted: 'bg-bg-muted',
      subtle: 'bg-bg-subtle',
    },
  },
  defaultVariants: { shape: 'rect', tone: 'muted' },
});

export interface SkeletonProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof skeletonStyles> {
  width?: string | number;
  height?: string | number;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, shape, tone, width, height, style, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(skeletonStyles({ shape, tone }), className)}
      style={{ width, height, ...style }}
      {...props}
    />
  );
});

export interface SkeletonTextProps extends Omit<SkeletonProps, 'shape' | 'height'> {
  /** Number of text lines to render. Last line is rendered at 70% width. */
  lines?: number;
  /** Visual size that maps to a typography line-height. */
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
}

const textHeight = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
} as const;

export function SkeletonText({ lines = 3, size = 'base', className, ...props }: SkeletonTextProps) {
  const h = textHeight[size];
  return (
    <div className={cn('flex flex-col gap-2', className)} role="status" aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          shape="text"
          height={h}
          width={i === lines - 1 && lines > 1 ? '70%' : '100%'}
          {...props}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton for a product / content card. Composes media + title + meta + price rows
 * so async lists can render a layout-stable placeholder.
 */
export interface SkeletonCardProps extends HTMLAttributes<HTMLDivElement> {
  media?: boolean;
  mediaAspect?: 'square' | 'video' | 'portrait';
  lines?: number;
}

const aspect = {
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-[3/4]',
} as const;

export function SkeletonCard({
  className,
  media = true,
  mediaAspect = 'square',
  lines = 2,
  ...props
}: SkeletonCardProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      className={cn(
        'border-border-subtle bg-bg-canvas flex flex-col gap-3 rounded-lg border p-3',
        className,
      )}
      {...props}
    >
      {media && <Skeleton className={cn('w-full', aspect[mediaAspect])} />}
      <SkeletonText size="sm" lines={lines} />
      <Skeleton shape="text" width="40%" height="1.25rem" />
    </div>
  );
}

export function SkeletonAvatar({
  size = 40,
  className,
  ...props
}: Omit<SkeletonProps, 'shape' | 'width' | 'height'> & { size?: number }) {
  return <Skeleton shape="circle" width={size} height={size} className={className} {...props} />;
}
